import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  ArrowLeft,
  Brain,
  Heart,
  Sparkles,
  Leaf,
  Compass,
  Shield,
  Smile
} from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';

const ONEDOC_URL = "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid";

const indications = [
  { icon: Brain, label: "Gestion du stress et des émotions" },
  { icon: Shield, label: "Blocages émotionnels & lâcher-prise" },
  { icon: Leaf, label: "Compulsions alimentaires & rapport au sucre" },
  { icon: Heart, label: "Confiance en soi & estime de soi" },
  { icon: Compass, label: "Accompagnement au changement de vie" },
  { icon: Smile, label: "Motivation, sérénité & bien-être" }
];

const benefits = [
  "Retrouver des ressources intérieures stables et durables",
  "Apaiser les compulsions alimentaires et le rapport émotionnel à la nourriture",
  "Avancer vers vos objectifs (perte de poids durable, projet de vie) avec plus de sérénité",
  "Renforcer la confiance en soi et l'estime de soi",
  "Cultiver le lâcher-prise et l'équilibre émotionnel au quotidien"
];

const PrestationHypnopraxie = () => {
  return (
    <div style={{ marginTop: '100px', background: 'var(--bg-page)' }}>
      <SEO
        title="Séance d'hypnopraxie à Vevey, Clarens & Montreux — 150 CHF | Nutri Centre Riviera"
        description="Séances d'hypnopraxie sur la Riviera vaudoise pour la confiance en soi, la gestion du stress et des émotions, les compulsions alimentaires, la perte de poids durable et l'accompagnement au changement. 150 CHF — Vevey, Clarens, Montreux."
        keywords="hypnopraxie Vevey, hypnopraxie Clarens, hypnopraxie Montreux, confiance en soi, gestion du stress, gestion des émotions, compulsions alimentaires, perte de poids durable, développement personnel, bien-être, équilibre émotionnel, accompagnement au changement, blocages émotionnels, estime de soi, motivation, sérénité, lâcher-prise"
        url="https://nutricentreriviera.ch/prestations/hypnopraxie"
      />

      <section
        data-testid="prestation-hypnopraxie-section"
        style={{ padding: '4rem 0 5rem', minHeight: '70vh' }}
      >
        <div className="container" style={{ maxWidth: '900px' }}>
          {/* Back link */}
          <Link
            to="/"
            data-testid="hypnopraxie-back-link"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--text-muted)',
              textDecoration: 'none',
              marginBottom: '2.5rem',
              fontSize: '0.95rem',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
          >
            <ArrowLeft size={18} />
            Retour aux prestations
          </Link>

          {/* Badge Nouveau */}
          <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
            <span
              data-testid="hypnopraxie-new-badge"
              style={{
                display: 'inline-block',
                background: 'var(--accent-primary)',
                color: 'white',
                padding: '0.4rem 1rem',
                borderRadius: '999px',
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase'
              }}
            >
              Nouveau
            </span>
          </div>

          {/* Title */}
          <h1
            data-testid="hypnopraxie-title"
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: 'clamp(2.3rem, 4.8vw, 3.5rem)',
              fontWeight: 700,
              lineHeight: 1.15,
              color: 'var(--text-primary)',
              textAlign: 'center',
              marginBottom: '1rem',
              letterSpacing: '-0.01em'
            }}
          >
            Séance d'hypnopraxie
          </h1>

          <p
            style={{
              fontSize: '1.1rem',
              lineHeight: 1.7,
              color: 'var(--text-muted)',
              textAlign: 'center',
              marginBottom: '2.5rem',
              maxWidth: '720px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          >
            Retrouvez vos ressources intérieures et avancez vers vos objectifs avec plus de sérénité.
          </p>

          {/* Price */}
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <span
              data-testid="hypnopraxie-price-badge"
              style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: '0.4rem',
                background: 'var(--accent-primary-light, #E9EEE8)',
                color: 'var(--accent-primary)',
                padding: '1.1rem 3rem',
                borderRadius: '999px',
                fontSize: '1.5rem',
                fontWeight: 700,
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              150 CHF
            </span>
          </div>

          {/* Intro */}
          <div style={{ maxWidth: '780px', margin: '0 auto 3rem', textAlign: 'left' }}>
            <p
              style={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: 'var(--text-primary)',
                marginBottom: '1.25rem'
              }}
            >
              L'<strong>hypnopraxie</strong> est un accompagnement doux et profond qui agit sur le plan émotionnel.
              Elle vous aide à dépasser les blocages, à apaiser le mental et à mobiliser vos propres ressources
              pour avancer durablement vers vos objectifs — qu'il s'agisse d'une <strong>perte de poids durable</strong>,
              d'un <strong>changement de vie</strong>, ou simplement de retrouver plus de <strong>sérénité</strong> au quotidien.
            </p>
            <p
              style={{
                fontSize: '1.05rem',
                lineHeight: 1.8,
                color: 'var(--text-secondary)',
                fontStyle: 'italic'
              }}
            >
              Les séances peuvent être réalisées <strong>indépendamment d'un suivi nutritionnel</strong>,
              ou en complément, pour soutenir un accompagnement plus global.
            </p>
          </div>

          {/* CE QUE L'HYPNOPRAXIE PERMET DE TRAVAILLER */}
          <div style={{ marginBottom: '3rem' }}>
            <h2
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: 'clamp(1.6rem, 3vw, 2rem)',
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: '1.75rem',
                textAlign: 'center'
              }}
            >
              Ce que l'hypnopraxie permet de travailler
            </h2>

            <div
              className="indications-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '1rem'
              }}
            >
              {indications.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={i}
                    data-testid={`hypnopraxie-indication-${i}`}
                    style={{
                      display: 'flex',
                      gap: '0.9rem',
                      alignItems: 'flex-start',
                      padding: '1.15rem 1.25rem',
                      background: 'white',
                      borderRadius: '0.85rem',
                      border: '1px solid var(--border-light)',
                      transition: 'all 0.3s ease'
                    }}
                    className="indication-card"
                  >
                    <div
                      style={{
                        width: '2.5rem',
                        height: '2.5rem',
                        borderRadius: '0.65rem',
                        background: 'var(--accent-primary-light, #E9EEE8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Icon size={20} style={{ color: 'var(--accent-primary)' }} strokeWidth={1.75} />
                    </div>
                    <span
                      style={{
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        lineHeight: 1.5,
                        paddingTop: '0.4rem'
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* BÉNÉFICES */}
          <div
            style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '2rem 2.25rem',
              border: '1px solid var(--border-light)',
              marginBottom: '3rem'
            }}
          >
            <h2
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: '1.25rem'
              }}
            >
              Les bénéfices d'un accompagnement en hypnopraxie
            </h2>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.9rem'
              }}
            >
              {benefits.map((b, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    gap: '0.85rem',
                    alignItems: 'flex-start',
                    fontSize: '1rem',
                    lineHeight: 1.65,
                    color: 'var(--text-primary)'
                  }}
                >
                  <Sparkles
                    size={18}
                    style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.25rem' }}
                    strokeWidth={1.75}
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Déroulement */}
          <div style={{ marginBottom: '3rem' }}>
            <h2
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: 'clamp(1.6rem, 3vw, 2rem)',
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: '1.25rem',
                textAlign: 'center'
              }}
            >
              Pour qui et comment ?
            </h2>
            <p
              style={{
                fontSize: '1.05rem',
                lineHeight: 1.8,
                color: 'var(--text-primary)',
                marginBottom: '1rem',
                maxWidth: '780px',
                margin: '0 auto 1rem'
              }}
            >
              L'hypnopraxie s'adresse à toutes les femmes (et hommes) souhaitant entamer une démarche de
              <strong> développement personnel</strong>, retrouver leur <strong>équilibre émotionnel</strong>,
              ou être accompagnés dans une période de transition. Aucun pré-requis n'est nécessaire :
              vous restez pleinement actrice de la séance, en éveil et en confiance.
            </p>
            <p
              style={{
                fontSize: '1.05rem',
                lineHeight: 1.8,
                color: 'var(--text-primary)',
                maxWidth: '780px',
                margin: '0 auto'
              }}
            >
              Les séances se déroulent dans nos cabinets de <strong>Vevey</strong> et <strong>Clarens (Montreux)</strong>,
              dans un cadre calme et bienveillant, propice au lâcher-prise.
            </p>
          </div>

          {/* CTA */}
          <div style={{ maxWidth: '780px', margin: '0 auto' }}>
            <a
              href={ONEDOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hypnopraxie-cta"
              style={{
                background: 'var(--accent-primary-light, #E9EEE8)',
                color: 'var(--accent-primary)',
                width: '100%',
                padding: '1.4rem 2rem',
                borderRadius: '999px',
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: '1.1rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                transition: 'all 0.3s ease',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--accent-primary)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 28px rgba(61, 84, 70, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--accent-primary-light, #E9EEE8)';
                e.currentTarget.style.color = 'var(--accent-primary)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Réserver une séance
              <ArrowUpRight size={22} />
            </a>
          </div>
        </div>

        <style>{`
          .indication-card:hover {
            transform: translateY(-2px);
            border-color: var(--accent-primary);
            box-shadow: 0 6px 18px rgba(61, 84, 70, 0.10);
          }
          @media (max-width: 700px) {
            .indications-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>

      <RelatedPages items={relatedPagesMap['hypnopraxie-vevey-montreux']} />
    </div>
  );
};

export default PrestationHypnopraxie;
